import { useEffect, useRef, useState } from 'react';
import { RefreshCw, Loader2, AlertTriangle, X } from 'lucide-react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { BYOK_PROVIDERS, KIND_MSG, discoverProviderModels, modelCache } from '../lib/models';
import { api } from '../lib/api';

const CUSTOM = '__custom__';
const NAME = { anthropic: 'Anthropic', openai: 'OpenAI' };
const fmt = (iso) => new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const label = (m) => (m.display_name ? `${m.display_name} · ${m.id}` : m.id);

async function sync(provider, apiKey, signal) {
  const r = await discoverProviderModels(provider, apiKey, signal);
  if (!r.blocked) return r;
  // Browser blocked (CORS): same server path as the PING, key used for this single request only
  try { return await api('/architect/byok/models', { method: 'POST', body: { provider, api_key: apiKey }, auth: true, timeout: 20000 }); }
  catch (e) { return { ok: false, kind: e.status === 429 ? 'rate_limit' : 'provider', error: e.message || KIND_MSG.provider }; }
}

function useDiscovery(provider, apiKey) {
  const [state, setState] = useState({ live: null, busy: false, error: null, syncedAt: null });
  const ctrl = useRef(null);
  const seq = useRef(0);
  const first = useRef(true);
  useEffect(() => {
    ctrl.current?.abort(); seq.current += 1;
    setState({ live: null, busy: false, error: null, syncedAt: null });
    if (first.current) { first.current = false; return; }
    modelCache.clear();
  }, [provider, apiKey]);
  useEffect(() => () => { ctrl.current?.abort(); seq.current += 1; }, []);
  const refresh = async () => {
    if (state.busy || !apiKey) return;
    const id = ++seq.current;
    ctrl.current = new AbortController();
    setState((s) => ({ ...s, busy: true, error: null }));
    const r = await sync(provider, apiKey, ctrl.current.signal);
    if (id !== seq.current || r.kind === 'cancelled') return;
    if (r.ok) { modelCache.set(provider, r.models); setState({ live: r.models, busy: false, error: null, syncedAt: new Date().toISOString() }); }
    else setState({ live: null, busy: false, syncedAt: null, error: r.error || KIND_MSG[r.kind] || KIND_MSG.provider });
  };
  const cancel = () => { ctrl.current?.abort(); seq.current += 1; setState((s) => ({ ...s, busy: false })); };
  return { ...state, refresh, cancel };
}

function ProviderSwitch({ provider, onChange }) {
  return (
    <div data-testid="byok-provider-switch" className="grid grid-cols-2 gap-2">
      {Object.keys(BYOK_PROVIDERS).map((p) => (
        <button key={p} data-testid={`byok-provider-${p}`} onClick={() => onChange(p)}
          className={`rounded-lg border px-3 py-2 text-[11px] font-semibold transition-colors ${provider === p ? 'border-cyan-400/60 bg-cyan-500/15 text-cyan-100' : 'border-white/10 text-slate-400 hover:bg-white/5'}`}>{NAME[p]}</button>
      ))}
    </div>
  );
}

function Groups({ groups, current, missing }) {
  return (
    <SelectContent className="bg-titan-surface border-violet-500/30 max-h-80">
      {missing && <SelectGroup><SelectLabel className="text-[10px] text-amber-300">Modello salvato</SelectLabel><SelectItem value={current} data-testid="byok-model-saved">{current} (non in elenco)</SelectItem></SelectGroup>}
      {groups.map((g) => (
        <SelectGroup key={g.title}>
          <SelectLabel data-testid={`byok-group-${g.kind}`} className="text-[10px] tracking-widest text-slate-500 uppercase">{g.title}</SelectLabel>
          {g.models.map((m) => <SelectItem key={`${g.kind}-${m.id}`} value={m.id} data-testid={`byok-model-${m.id}`}>{label(m)}</SelectItem>)}
        </SelectGroup>
      ))}
      <SelectGroup><SelectItem value={CUSTOM} data-testid="byok-model-custom">Inserisci ID modello personalizzato…</SelectItem></SelectGroup>
    </SelectContent>
  );
}

export default function ByokModels({ settings, updateSettings, onChange }) {
  const provider = settings.byokProvider || 'anthropic';
  const d = useDiscovery(provider, settings.apiKey);
  const [sort, setSort] = useState('provider');
  const cache = d.live ? null : modelCache.get(provider);
  const fallback = BYOK_PROVIDERS[provider].models;
  const order = (list) => (sort === 'az' ? [...list].sort((a, b) => a.id.localeCompare(b.id)) : list);
  const groups = d.live
    ? [{ kind: 'live', title: provider === 'openai' ? `Dal tuo account · ${d.live.length} · compatibilità da verificare` : `Dal tuo account · ${d.live.length}`, models: order(d.live) }]
    : [...(cache ? [{ kind: 'cache', title: `Cache non aggiornata · ${fmt(cache.syncedAt)}`, models: order(cache.models) }] : []), { kind: 'fallback', title: "Predefiniti dell'app · non verificati", models: fallback }];
  const known = groups.some((g) => g.models.some((m) => m.id === settings.byokModel));
  const [custom, setCustom] = useState(() => !known);
  const [draft, setDraft] = useState(settings.byokModel);
  const missing = !custom && !known && !!settings.byokModel;
  const pick = (v) => { if (v === CUSTOM) { setCustom(true); setDraft(settings.byokModel); return; } setCustom(false); updateSettings({ byokModel: v }); onChange?.(); };
  const setProvider = (p) => { if (p === provider) return; modelCache.clear(); setCustom(false); updateSettings({ byokProvider: p, byokModel: BYOK_PROVIDERS[p].defaultModelId }); onChange?.(); };
  const saveCustom = (v) => { setDraft(v); updateSettings({ byokModel: v.trim() }); onChange?.(); };
  const noKey = !settings.apiKey;
  return (
    <div data-testid="byok-models" className="space-y-2">
      <ProviderSwitch provider={provider} onChange={setProvider} />
      <div className="flex gap-2">
        <Select value={custom ? CUSTOM : settings.byokModel} onValueChange={pick}>
          <SelectTrigger data-testid="byok-model-select" className="bg-black/30 min-w-0"><SelectValue /></SelectTrigger>
          <Groups groups={groups} current={settings.byokModel} missing={missing} />
        </Select>
        {d.busy
          ? <button data-testid="byok-sync-cancel" onClick={d.cancel} title="Annulla" className="shrink-0 flex items-center gap-1 rounded-lg border border-white/15 px-3 text-[11px] text-slate-300"><Loader2 className="h-3.5 w-3.5 animate-spin" /><X className="h-3.5 w-3.5" /></button>
          : <button data-testid="byok-sync-btn" onClick={d.refresh} disabled={noKey} title={noKey ? 'Inserisci prima la chiave API' : 'Aggiorna modelli'} className="shrink-0 flex items-center gap-1.5 rounded-lg border border-cyan-500/40 px-3 text-[11px] font-semibold text-cyan-200 hover:bg-cyan-500/10 disabled:opacity-40"><RefreshCw className="h-3.5 w-3.5" /><span className="hidden sm:inline">Aggiorna modelli</span></button>}
      </div>
      {custom && (
        <div className="space-y-1">
          <Input data-testid="byok-custom-input" value={draft} onChange={(e) => saveCustom(e.target.value)} placeholder={provider === 'anthropic' ? 'es. claude-sonnet-4-6' : 'es. gpt-4o'} className="bg-black/30 font-mono text-xs" />
          <p className="text-[10px] text-slate-500">ID esatto del provider, senza prefisso. Usa “Test connessione” per verificarlo.</p>
        </div>
      )}
      <Status d={d} noKey={noKey} missing={missing} current={settings.byokModel} provider={provider} sort={sort} setSort={setSort} />
    </div>
  );
}

function Status({ d, noKey, missing, current, provider, sort, setSort }) {
  return (
    <div data-testid="byok-sync-status" className="space-y-1 text-[10px] t-chat">
      {noKey && <div className="text-slate-500">Inserisci la chiave {NAME[provider]} per leggere i modelli del tuo account.</div>}
      {d.busy && <div className="text-cyan-300">Lettura dei modelli dal tuo account {NAME[provider]}…</div>}
      {d.syncedAt && <div data-testid="byok-sync-ok" className="text-emerald-300">Elenco aggiornato dal provider alle {fmt(d.syncedAt)}{provider === 'openai' ? ' · disponibilità segnalata da OpenAI, compatibilità con la creazione dei giochi da verificare con il Test' : ''}</div>}
      {d.error && <div data-testid="byok-sync-error" className="flex items-start gap-1.5 text-rose-300"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />{d.error} · puoi usare la cache, i predefiniti o un ID personalizzato</div>}
      {missing && <div data-testid="byok-model-missing" className="text-amber-300">Il modello salvato “{current}” non è nell'elenco: selezionane uno valido o usa un ID personalizzato.</div>}
      {d.syncedAt && <button data-testid="byok-sort-toggle" onClick={() => setSort(sort === 'az' ? 'provider' : 'az')} className="text-slate-400 underline underline-offset-2">Ordine: {sort === 'az' ? 'A-Z' : 'del provider'}</button>}
    </div>
  );
}
